import { ART_PX, PixelBuffer, Pt, bayer, drawLine, drawPolyline, hash01, mix, rgb } from "./pixel_raster"
import { Bounds, HORIZON, MOON, REST, SCENE, clamp01, rng, smooth } from "./shipwreck_geometry"

/**
 * The storm the ship is driven ashore in: cloud torn open round the moon, a storm cell over the land,
 * a squall line on the horizon, a heaving sea, wind-driven rain and lightning. Pure, like
 * pixel_shipwreck_art: buffers in, buffers out, so it can be painted in a test or to a PNG.
 *
 * Colours are the Liberated Pixel Cup style guide's palette (colors.png): the purple-grey stone ramp
 * for cloud, the grey-teal ones for the sea, so the storm sits in the same world as the game.
 * Authored like the rest of the scene with the ship on the LEFT: the wind blows left to right
 * (onshore, toward the land), and the scene mirrors it all at upload time.
 */

const PX = ART_PX
export const HORIZON_ROW = Math.round(HORIZON / PX)

const lpc = (...hex: number[]) => hex.map(h => rgb(h))

/** The night behind the cloud, darkest overhead. */
const NIGHT = lpc(0x130d14, 0x1d131e, 0x2e2533)
/** Cloud, outline .. lit top: the style guide's purple stone ramp. */
const CLOUD = lpc(0x130d14, 0x1d131e, 0x2e2533, 0x3d3748, 0x4d4a5d, 0x726b7e)
/** Cloud edges the moon catches, dim .. the silver lining at the gap. */
const RIM = lpc(0x726b7e, 0x959fc5, 0xa9c9ca, 0xc8e8e8)
/** The light horizon under the squall, where the far side of the storm is clear. */
const HAZE = lpc(0x2e2533, 0x3d3748, 0x46425d, 0x4d4a5d)

// ---- cloud ------------------------------------------------------------------------------------

export const MOON_ART: Pt = [MOON.x / PX, MOON.y / PX]

/** One billow of a cloud: a shaded disc. Clouds are heaps of them, painted back to front. */
export interface Lobe {
    x: number
    y: number
    r: number
    /** Height over width: distant billows are flattened by perspective. 1 when absent. */
    sq?: number
}

export interface CloudStyle {
    /** Lit from this direction (unit) instead of from the moon: cloud that moves can't bake the moon in. */
    light?: Pt
    /** Below this row a billow is cut flat: the cloud's base. */
    base?: number
}

/** The direction toward the moon at an art pixel (unit, in the sky's plane), and how far it is. */
const lightAt = (x: number, y: number): [number, number, number] => {
    const dx = MOON_ART[0] - x
    const dy = MOON_ART[1] - y
    const d = Math.hypot(dx, dy) || 1
    return [dx / d, dy / d, d]
}

/** A cloud's tones: outline/shadow .. its lit edge. Rims near the moon go brighter (RIM). */
export interface CloudTones {
    shadow: number
    body: number
    lit: number
    rim: number
}

const NEAR_TONES: CloudTones = { shadow: CLOUD[0], body: CLOUD[1], lit: CLOUD[2], rim: CLOUD[3] }

/**
 * Paint a heap of billows as ONE cloud, the way it would be drawn by hand: a flat body, its outline
 * facing the moon picked out in a lit rim (silver near the moon), the side away from it a shadow, and
 * where one billow overlaps another a lit edge on the front one's moon side and a crease on its far
 * side. Billows are painted back to front, so a later one is in front.
 */
export const paintLobes = (out: PixelBuffer, lobes: Lobe[], style: CloudStyle & { tones?: CloudTones } = {}) => {
    if (!lobes.length) return
    const base = style.base ?? Infinity
    const tones = style.tones ?? NEAR_TONES
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity
    for (const l of lobes) {
        x0 = Math.min(x0, Math.floor(l.x - l.r) - 2)
        x1 = Math.max(x1, Math.ceil(l.x + l.r) + 2)
        y0 = Math.min(y0, Math.floor(l.y - l.r) - 2)
        y1 = Math.max(y1, Math.min(base, Math.ceil(l.y + l.r)) + 2)
    }
    const w = x1 - x0 + 1
    const h = y1 - y0 + 1
    const ids = new Int16Array(w * h)
    lobes.forEach((l, k) => {
        const ry = l.r * (l.sq ?? 1)
        for (let y = Math.floor(l.y - ry); y <= Math.ceil(l.y + ry); y++) {
            if (y > base) continue
            for (let x = Math.floor(l.x - l.r); x <= Math.ceil(l.x + l.r); x++) {
                const nx = (x + 0.5 - l.x) / l.r
                const ny = (y + 0.5 - l.y) / ry
                if (nx * nx + ny * ny <= 1) ids[(y - y0) * w + (x - x0)] = k + 1
            }
        }
    })
    const id = (x: number, y: number) => (x < x0 || y < y0 || x > x1 || y > y1 ? 0 : ids[(y - y0) * w + (x - x0)])
    for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
            const me = id(x, y)
            if (!me) continue
            const [lx, ly, md] = style.light ? [style.light[0], style.light[1], Infinity] : lightAt(x, y)
            const sx = Math.round(lx * 1.42)
            const sy = Math.round(ly * 1.42)
            const toward = id(x + sx, y + sy)
            const toward2 = id(x + 2 * sx, y + 2 * sy)
            const away = id(x - sx, y - sy)
            const l = lobes[me - 1]
            // Moonlight: billows near the gap are lit right through, the rest only along their edges.
            const glow = md < 36 ? 2 : md < 70 ? 1 : 0
            const body = glow === 2 ? CLOUD[3] : glow === 1 ? CLOUD[2] : tones.body
            const lit = glow === 2 ? CLOUD[4] : glow === 1 ? CLOUD[3] : tones.lit
            let c = body
            if (!toward) {
                // The outline facing the moon: the silver lining when it is close.
                c = md < 34 ? RIM[3] : md < 50 ? RIM[2] : md < 72 ? RIM[1] : md < 110 ? RIM[0] : tones.rim
            } else if (!toward2) {
                c = md < 50 ? RIM[0] : md < 90 ? tones.rim : lit
            } else if (!away || !id(x, y + 1)) {
                c = tones.shadow
            } else if (l.r >= 9 && toward !== me && toward < me && md < 140) {
                // A big billow's edge crossing one behind it, on the moon side: lit.
                c = lit
            } else if (l.r >= 9 && away !== me && away > 0 && away < me && hash01(me * 7) < 0.6) {
                // ...and its far side a crease, on some of them: enough to model the heap.
                c = tones.shadow
            }
            out.set(x, y, c)
        }
    }
}

// ---- the sky ----------------------------------------------------------------------------------

/** The sky behind the cloud: dark overhead, glowing round the moon, light along the horizon. */
const nightAt = (x: number, y: number): number => {
    if (y >= 136) {
        // Brightest under the moon, where the far side of the storm is clear; dim toward the land.
        const moonward = 0.55 + 0.45 * Math.exp(-Math.pow((x - MOON_ART[0]) / 130, 2))
        const g = clamp01((y - 136) / (HORIZON_ROW - 136)) * 2.6 * moonward
        const i = Math.floor(g)
        return HAZE[Math.min(3, bayer(x, y) < (g - i) * 0.7 ? i + 1 : i)]
    }
    const d = Math.hypot(x + 0.5 - MOON_ART[0], (y + 0.5 - MOON_ART[1]) * 1.2)
    if (d < 16) return NIGHT[2]
    if (d < 21) return bayer(x, y) < 0.5 ? NIGHT[2] : NIGHT[1]
    if (d < 30) return NIGHT[1]
    if (d < 36) return bayer(x, y) < 0.5 ? NIGHT[1] : NIGHT[0]
    return y > 100 && bayer(x, y) < (y - 100) / 40 ? NIGHT[1] : NIGHT[0]
}

/** The bounds of the sky in art pixels. */
export const skyRect = (b: Bounds) => {
    const x0 = Math.floor(b.left / PX)
    const y0 = Math.floor(b.top / PX)
    return { x0, y0, w: Math.ceil(b.right / PX) - x0, h: HORIZON_ROW - y0 }
}

/** An ellipse a cloud heap is sown in. */
export interface Blob {
    x: number
    y: number
    rx: number
    ry: number
}

/**
 * Billows sown at random inside a few ellipses, sizes between `r0` and `r1`: their discs poke out of
 * the ellipses' edges, which is what makes the outline bumpy. Lower billows are in front.
 */
export const heap = (seed: number, blobs: Blob[], n: number, r0: number, r1: number, sq: number = 1): Lobe[] => {
    const random = rng(seed)
    const out: Lobe[] = []
    for (let tries = 0; out.length < n && tries < n * 40; tries++) {
        const b = blobs[Math.floor(random() * blobs.length)]
        const u = random() * 2 - 1
        const v = random() * 2 - 1
        if (u * u + v * v > 1) continue
        // Mostly small billows, a few big ones: the big ones make the mass, the small ones its edge.
        out.push({ x: b.x + u * b.rx, y: b.y + v * b.ry, r: r0 + Math.pow(random(), 1.8) * (r1 - r0), sq })
    }
    return out.sort((a, b) => a.y + a.r - (b.y + b.r))
}

const FAR_TONES: CloudTones = { shadow: CLOUD[0], body: CLOUD[1], lit: CLOUD[2], rim: CLOUD[3] }
const CELL_TONES: CloudTones = { shadow: CLOUD[0], body: CLOUD[1], lit: CLOUD[2], rim: CLOUD[4] }

/**
 * The sky, which never moves: the overcast hanging over the whole scene, torn open round the moon;
 * the storm cell towering over the land; the squall line along the horizon with rain hanging from it.
 */
export const buildStormSky = (b: Bounds): PixelBuffer => {
    const r = skyRect(b)
    const buf = new PixelBuffer(r.w, r.h, r.x0, r.y0)
    const x1 = r.x0 + r.w
    for (let y = r.y0; y < r.y0 + r.h; y++) {
        for (let x = r.x0; x < x1; x++) {
            buf.set(x, y, nightAt(x, y))
        }
    }
    const [mx] = MOON_ART

    // The squall line, far off: a long low bank, flattened by distance, with rain hanging under it.
    const squall: Blob[] = []
    for (let x = r.x0 - 20; x < x1 + 30; x += 30) squall.push({ x, y: 129 + 1.5 * Math.sin(x / 17), rx: 26, ry: 2 })
    paintLobes(buf, heap(21, squall, Math.round((r.w / 400) * 90), 3, 9, 0.55), { base: 135, tones: FAR_TONES })
    paintRainCurtains(buf, r.x0, x1)

    // The storm cell over the land: a dark mass down to the horizon, lit on its moon side.
    const cell: Blob[] = [
        { x: 350, y: 112, rx: 60, ry: 10 },
        { x: 344, y: 88, rx: 40, ry: 18 },
        { x: 352, y: 58, rx: 34, ry: 16 },
        { x: 366, y: 30, rx: 80, ry: 10 },
    ]
    paintLobes(buf, heap(5, cell, 110, 4, 16, 0.8), { base: 124, tones: CELL_TONES })

    // The overcast: billows hanging from the top of the screen, lifting clear of the moon.
    const ceiling: Blob[] = []
    for (let x = r.x0 - 20; x < x1 + 20; x += 16) {
        const lift = 16 * Math.exp(-Math.pow((x - mx) / 30, 2))
        ceiling.push({ x, y: 6 - lift + 3 * Math.sin(x / 19), rx: 14, ry: 7 })
    }
    for (let y = r.y0; y < 2; y++) {
        for (let x = r.x0; x < x1; x++) buf.set(x, y, CLOUD[1])
    }
    paintLobes(buf, heap(9, ceiling, Math.round((r.w / 400) * 120), 4, 13, 0.75))

    // A bank under the moon, its top edge lit: the bottom of the window the moon shines through.
    paintLobes(
        buf,
        heap(31, [{ x: mx + 14, y: 64, rx: 44, ry: 3 }, { x: mx - 46, y: 56, rx: 26, ry: 3 }, { x: mx + 70, y: 59, rx: 22, ry: 3 }], 46, 3, 9, 0.6),
        { base: 70 },
    )
    return buf
}

/** Curtains of rain hanging from the squall's base, darker against the light horizon. */
const paintRainCurtains = (out: PixelBuffer, x0: number, x1: number) => {
    const random = rng(41)
    for (let k = 0; k < 11; k++) {
        const cx = x0 + random() * (x1 - x0)
        const w = 12 + random() * 30
        const top = 134
        for (let y = top; y < HORIZON_ROW; y++) {
            const v = (y - top) / (HORIZON_ROW - top)
            // Slanting with the wind, thinning out toward the sea.
            const shift = v * 6
            for (let x = Math.floor(cx - w / 2 + shift); x < cx + w / 2 + shift && x < x1; x++) {
                const u = Math.abs((x - shift - cx) / (w / 2))
                const streak = hash01(Math.floor((x - shift * 0.5) / 2) * 7 + k * 131) < 0.6
                const a = (1 - u * u) * (1 - v * 0.75) * (streak ? 1 : 0.3)
                if (a > 0.2 + bayer(x, y) * 0.6) {
                    const c = out.get(x, y)
                    const i = HAZE.indexOf(c)
                    if (i > 0) out.set(x, y, HAZE[i - 1])
                }
            }
        }
    }
}

// ---- scud -------------------------------------------------------------------------------------

/** A band of torn low cloud racing downwind: a strip that repeats every `tile.w` art pixels. */
export interface ScudStrip {
    tile: PixelBuffer
    /** Art pixels per choreography second, downwind (+x). */
    speed: number
}

const SCUD_TONES: CloudTones = { shadow: CLOUD[0], body: CLOUD[1], lit: CLOUD[2], rim: CLOUD[3] }

/** Rags of cloud along a band, painted three times over so the strip wraps without a seam. */
const scudStrip = (seed: number, w: number, y: number, h: number, n: number, r0: number, r1: number): PixelBuffer => {
    const random = rng(seed)
    const blobs: Blob[] = []
    for (let k = 0; k < n; k++) {
        blobs.push({ x: (k + 0.2 + random() * 0.6) * (w / n), y: y + (random() - 0.5) * h, rx: 8 + random() * 16, ry: 1.5 })
    }
    const lobes = heap(seed + 1, blobs, n * 7, r0, r1, 0.6)
    const wide = new PixelBuffer(w * 3, h + 24, -w, Math.floor(y - h / 2 - 12))
    const copies = [-w, 0, w].flatMap(dx => lobes.map(l => ({ ...l, x: l.x + dx })))
    paintLobes(wide, copies, { light: [-0.45, -0.89], tones: SCUD_TONES })
    const tile = new PixelBuffer(w, wide.h, 0, wide.y0)
    for (let j = 0; j < wide.h; j++) {
        tile.data.set(wide.data.subarray(j * wide.w + w, j * wide.w + 2 * w), j * w)
    }
    return tile
}

/** High scud that crosses the moon, and lower rags racing faster under the squall. */
export const buildScud = (): ScudStrip[] => [
    { tile: scudStrip(71, 420, 44, 14, 3, 2.5, 6.5), speed: 7 },
    { tile: scudStrip(72, 340, 106, 12, 4, 2, 5.5), speed: 13 },
]

/** How far a strip has drifted at `t`, in whole art pixels. */
export const scudOffset = (strip: ScudStrip, t: number): number => Math.round((strip.speed * t) / 1000) % strip.tile.w

// ---- the sea ----------------------------------------------------------------------------------

// The sea is backlit by the moon: dark wave faces, bright crests, whitecaps, foam.

/** Wave faces: the near sea's dark navy, the far sea's purple where it mirrors the haze. */
const SEA = lpc(0x101820, 0x1d131e, 0x2e2533, 0x31313e)
/** Crests: far (haze) .. lit .. in the moon's path. */
const CREST = lpc(0x3d3748, 0x4a5057, 0x586b90, 0x748da4)
export const FOAM_TONES = lpc(0x8aaaab, 0xa9c9ca, 0xc8e8e8)

/** One row of swell, from the horizon (k = 0) toward the viewer. */
export interface SwellRow {
    k: number
    /** The row's resting line, in art pixels. */
    y: number
    /** Gap to the next row: the row's face is painted this deep. */
    gap: number
    amp: number
    len: number
    /** Art pixels per choreography second, downwind (toward +x). */
    speed: number
    phase: number
}

export const buildSwell = (b: Bounds): SwellRow[] => {
    const bottom = Math.ceil(b.bottom / PX) + 2
    const random = rng(17)
    const out: SwellRow[] = []
    let y = HORIZON_ROW + 1
    for (let k = 0; y < bottom; k++) {
        const gap = 1 + (y - HORIZON_ROW) * 0.15
        out.push({
            k,
            y,
            gap,
            amp: Math.min(6, 0.25 + gap * 0.55),
            len: 7 + gap * 3.2 + random() * 5,
            speed: 3 + gap * 2.6,
            phase: random() * Math.PI * 2,
        })
        y += gap
    }
    return out
}

/** A wave's height at x: sharp crests, flat troughs (second-order Stokes), coming in sets. */
const swellAt = (row: SwellRow, x: number, t: number): number => {
    const th = (2 * Math.PI * (x - (row.speed * t) / 1000)) / row.len + row.phase
    const set = 0.55 + 0.45 * Math.sin((2 * Math.PI * (x - (row.speed * t) / 1250)) / (row.len * 4.7) + row.k * 0.8)
    return (row.amp * set * (Math.cos(th) + 0.32 * Math.cos(2 * th))) / 1.32
}

/** The long heave under the chop: whole stretches of sea lifting and falling, nearer rows more. */
const heaveAt = (row: SwellRow, x: number, t: number): number => {
    const near = clamp01((row.y - HORIZON_ROW) / 70)
    return near * near * 3.5 * Math.sin((2 * Math.PI * (x - (row.speed * t) / 1600)) / 150 + row.k * 0.42)
}

/** Half the width of the moon's broken path on the water at a row. */
const glitterHalf = (y: number) => 5 + (y - HORIZON_ROW) * 0.62

/**
 * The swell rows `from` .. `to` (inclusive `k`s), back to front, into `out`. The scene paints the rows
 * behind the ship's waterline under her and the rest over her, so the near waves wash over her hull.
 * `appear` (0..1) dissolves the sea in from the horizon.
 */
export const paintSwell = (out: PixelBuffer, rows: SwellRow[], t: number, from: number = 0, to: number = Infinity, appear: number = 1, flash?: Flash) => {
    const x0 = out.x0
    const x1 = out.x0 + out.w
    const mx = MOON_ART[0]
    const heights = new Float32Array(out.w + 1)
    for (const row of rows) {
        if (row.k < from || row.k > to) continue
        const near = clamp01((row.y - HORIZON_ROW) / 70)
        const depth = Math.ceil(row.gap * 1.7) + 1
        const breakCycle = Math.floor(t / 230)
        for (let i = 0; i <= out.w; i++) heights[i] = swellAt(row, x0 + i, t)
        for (let x = x0; x < x1; x++) {
            if (appear < 1 && appear * 1.25 - near * 0.25 <= bayer(x, row.k)) continue
            const h = heights[x - x0]
            const lift = heaveAt(row, x, t)
            const crest = Math.round(row.y - h - lift)
            const slope = heights[x - x0 + 1] - h
            // The moon's path: broken into glints, thickest straight under the moon.
            const off = Math.abs(x - mx) / (glitterHalf(row.y) * (0.8 + 0.2 * Math.sin(row.k * 2.1 + t / 900)))
            const gx = x - Math.floor((row.speed * t) / 1000)
            const cell = 3 + Math.round(near * 3)
            const glint = Math.floor(gx / cell)
            const glitter = off < 1 && gx - glint * cell < 2 && hash01(glint * 613 + row.k * 37 + Math.floor(t / 220)) < 0.7 * (1 - off * off)
            // Whitecaps: a run of foam on a high crest, that comes and goes as the wave breaks.
            const along = Math.floor((x - (row.speed * t) / 1000) / (3 + near * 5))
            const cap = h > row.amp * 0.78 && hash01(along * 977 + row.k * 131 + Math.floor(breakCycle / 3) * 7) < 0.2 + near * 0.4
            const high = h > row.amp * 0.35
            const farSea = near < 0.28
            const face = farSea ? SEA[1] : SEA[0]
            for (let y = crest; y < Math.round(row.y - lift) + depth; y++) {
                const d = y - crest
                let c: number
                if (d === 0) {
                    if (cap) c = near > 0.45 ? FOAM_TONES[2] : FOAM_TONES[1]
                    else if (glitter && high) c = slope > -0.25 && hash01(x * 31 + row.k * 7 + Math.floor(t / 160)) < 0.55 ? FOAM_TONES[2] : FOAM_TONES[0]
                    else if (glitter) c = CREST[3]
                    else if (farSea) c = high ? CREST[0] : face
                    else c = high && slope > 0 ? CREST[2] : high ? CREST[1] : face
                } else if (d === 1 && row.amp > 1.4) {
                    c = cap ? FOAM_TONES[0] : glitter ? CREST[2] : high && slope > 0 ? SEA[3] : face
                } else if (d === 2 && (cap || glitter) && row.amp > 2.6) {
                    c = cap ? FOAM_TONES[0] : CREST[1]
                } else if (cap && d < 3 + Math.round(near * 3) && hash01(x * 13 + d) < 0.45) {
                    // Foam spilling down the face of a near breaker.
                    c = FOAM_TONES[0]
                } else {
                    c = face
                    if (glitter && d < 4 && bayer(x, y) < 0.3) c = SEA[3]
                }
                out.set(x, y, flash ? flashSea(c, flash.level(x, y)) : c)
            }
        }
    }
}

/** The swell row the ship's waterline sits on: rows after it are painted over her. */
export const rowAt = (rows: SwellRow[], y: number): number => {
    let k = 0
    for (const row of rows) {
        if (row.y <= y) k = row.k
    }
    return k
}

// ---- rain -------------------------------------------------------------------------------------

/** One sheet of rain: a tile of streaks that repeats, drifting down and downwind. */
export interface RainSheet {
    /** White streaks on clear: the scene tints them (rain colour, or white in a flash). */
    tile: PixelBuffer
    /** Art pixels per choreography second. */
    vx: number
    vy: number
    tint: number
    alpha: number
}

/** How far the wind blows the rain across for every pixel it falls. */
export const RAIN_SLANT = 0.42

const rainTile = (seed: number, w: number, h: number, n: number, len0: number, len1: number): PixelBuffer => {
    const tile = new PixelBuffer(w, h)
    const random = rng(seed)
    const white = rgb(0xffffff)
    for (let k = 0; k < n; k++) {
        const x = Math.floor(random() * w)
        const y = Math.floor(random() * h)
        const len = len0 + Math.floor(random() * (len1 - len0 + 1))
        drawLine(x + 0.5, y + 0.5, x + 0.5 + len * RAIN_SLANT, y + 0.5 + len, (px, py) => tile.set(((px % w) + w) % w, ((py % h) + h) % h, white))
    }
    return tile
}

/** Far rain, fine and slow; near rain, longer streaks falling fast. */
export const buildRain = (): RainSheet[] => [
    { tile: rainTile(61, 128, 128, 124, 2, 3), vx: 0, vy: 520, tint: 0x586b90, alpha: 0.5 },
    { tile: rainTile(62, 128, 128, 34, 5, 7), vx: 0, vy: 1050, tint: 0x8188be, alpha: 0.42 },
].map(sheet => ({ ...sheet, vx: sheet.vy * RAIN_SLANT }))

/** The rain's strength, 0..1: falling in as the night does, and coming in gusts. */
export const rainAt = (t: number): number => {
    const arrive = smooth((t - SCENE.water[0]) / 1500)
    const gust = 0.82 + 0.18 * Math.sin(t / 1700) * Math.sin(t / 610 + 1)
    return arrive * gust
}

/** Where a sheet's tile sits at `t`: whole art pixels, so the streaks never smear between two. */
export const rainOffset = (sheet: RainSheet, t: number): Pt => [Math.round((sheet.vx * t) / 1000), Math.round((sheet.vy * t) / 1000)]

// ---- lightning --------------------------------------------------------------------------------

export interface Bolt {
    /** When it strikes, choreography ms. */
    t: number
    main: Pt[]
    branches: Pt[][]
    /** Where its light is centred and how far it reaches, art pixels. */
    cx: number
    cy: number
    reach: number
    /** 2: a strike that lights the sky; 1: a distant one. */
    power: number
}

/** A jagged path from a to b by midpoint displacement: `rough` of each span's length, either side. */
const jag = (random: () => number, a: Pt, b: Pt, depth: number, rough: number): Pt[] => {
    let pts: Pt[] = [a, b]
    for (let d = 0; d < depth; d++) {
        const next: Pt[] = [pts[0]]
        for (let i = 1; i < pts.length; i++) {
            const p = pts[i - 1]
            const q = pts[i]
            const dx = q[0] - p[0]
            const dy = q[1] - p[1]
            const len = Math.hypot(dx, dy) || 1
            const o = (random() - 0.5) * len * rough
            next.push([(p[0] + q[0]) / 2 - (dy / len) * o, (p[1] + q[1]) / 2 + (dx / len) * o], q)
        }
        pts = next
    }
    return pts
}

export const makeBolt = (seed: number, t: number, from: Pt, to: Pt, power: number): Bolt => {
    const random = rng(seed)
    const main = jag(random, from, to, 5, 0.55)
    const branches: Pt[][] = []
    const n = power > 1 ? 3 : 1
    for (let k = 0; k < n; k++) {
        const i = Math.floor(main.length * (0.15 + random() * 0.5))
        const p = main[i]
        const q = main[Math.min(main.length - 1, i + 3)]
        const ang = Math.atan2(q[1] - p[1], q[0] - p[0]) + (random() < 0.5 ? -1 : 1) * (0.45 + random() * 0.5)
        const len = Math.hypot(to[0] - from[0], to[1] - from[1]) * (0.2 + random() * 0.25)
        branches.push(jag(random, p, [p[0] + Math.cos(ang) * len, p[1] + Math.sin(ang) * len], 4, 0.6))
    }
    return { t, main, branches, cx: (from[0] + to[0]) / 2, cy: (from[1] + to[1]) / 2, reach: power > 1 ? 150 : 80, power }
}

/** How a strike plays out, choreography ms after it: bright, a breath of dark, the restrike, the glow. */
const STRIKE: [number, number, number][] = [
    // [until, sky level, bolt brightness]
    [30, 2, 2],
    [55, 0, 1],
    [90, 2, 2],
    [170, 1, 1],
    [260, 0, 0.5],
]
export const STRIKE_MS = STRIKE[STRIKE.length - 1][0]

/** Bolt over the land (the storm cell), behind the ship, or low on the horizon off to one side. */
const SITES: { from: Pt; to: Pt; spread: number }[] = [
    { from: [374, 66], to: [362, 142], spread: 14 },
    { from: [26, 70], to: [34, 156], spread: 10 },
    { from: [4, 130], to: [8, 156], spread: 4 },
]

/** The bolts of the opening, then one every five seconds or so (of choreography) for as long as it runs. */
export const boltsNear = (t: number): Bolt[] => {
    const out: Bolt[] = []
    const opening: [number, number, number][] = [
        [1900, 0, 1],
        [3250, 0, 2],
        [SCENE.impact - 20, 1, 2],
    ]
    for (const [bt, site, power] of opening) {
        if (t >= bt && t < bt + STRIKE_MS) {
            const s = SITES[site]
            out.push(makeBolt(bt, bt, s.from, s.to, power))
        }
    }
    const PERIOD = 5200
    const cycle = Math.floor((t - REST) / PERIOD)
    for (const c of [cycle - 1, cycle]) {
        if (c < 0) continue
        const random = rng(900 + c)
        const bt = REST + 1400 + c * PERIOD + random() * 2200
        if (t < bt || t >= bt + STRIKE_MS) continue
        const pick = random()
        const site = pick < 0.6 ? 0 : pick < 0.85 ? 1 : 2
        const s = SITES[site]
        const dx = (random() - 0.5) * 2 * s.spread
        out.push(makeBolt(5000 + c, bt, [s.from[0] + dx, s.from[1]], [s.to[0] + dx * 0.6, s.to[1]], site === 2 ? 1 : random() < 0.7 ? 2 : 1))
    }
    return out
}

/** The stage of a strike at `t`: how lit the sky is (0..2) and how bright the bolt (0..2). */
export const strikeAt = (bolt: Bolt, t: number): { sky: number; bolt: number } => {
    const dt = t - bolt.t
    for (const [until, sky, b] of STRIKE) {
        if (dt < until) return { sky: Math.min(sky, bolt.power), bolt: b }
    }
    return { sky: 0, bolt: 0 }
}

const BOLT_CORE = rgb(0xffffff)
const BOLT_HOT = rgb(0xe5e6c7)
const BOLT_GLOW = rgb(0x959fc5)
const BOLT_DIM = rgb(0x695b95)

/** Draw a bolt: a white core with a violet glow beside the main channel, the branches fainter. */
export const paintBolt = (out: PixelBuffer, bolt: Bolt, brightness: number) => {
    if (brightness <= 0) return
    const core = brightness >= 2 ? BOLT_CORE : brightness >= 1 ? BOLT_GLOW : BOLT_DIM
    if (brightness >= 2) {
        drawPolyline(bolt.main, (x, y) => {
            for (const [dx, dy] of [[-1, 0], [1, 0]]) out.set(x + dx, y + dy, BOLT_GLOW)
        })
    }
    for (const branch of bolt.branches) {
        drawPolyline(branch, (x, y) => out.set(x, y, brightness >= 2 ? BOLT_HOT : BOLT_DIM))
    }
    drawPolyline(bolt.main, (x, y) => out.set(x, y, core))
}

/**
 * A strike's light at an art pixel, in steps: the whole sky lifts a step when the sky is lit, and
 * near the bolt a step more, the edge between them dithered. A distant strike only lights round itself.
 */
export const flashLevelAt = (bolt: Bolt, sky: number, x: number, y: number): number => {
    if (sky <= 0) return 0
    const d = Math.hypot(x - bolt.cx, (y - bolt.cy) * 1.3) / bolt.reach
    const inner = d < 0.6 + (bayer(x, y) - 0.5) * 0.35
    if (bolt.power < 2) return inner ? 1 : 0
    return inner ? sky : 1
}

/** The sky's ramp under lightning, dark .. white: the style guide's violet and pale blue rows. */
const FLASH = lpc(0x2e2533, 0x46425d, 0x695b95, 0x8188be, 0xa9c9ca, 0xc8e8e8, 0xffffff)

const luma = (c: number): number => 0.3 * (c & 0xff) + 0.59 * ((c >>> 8) & 0xff) + 0.11 * ((c >>> 16) & 0xff)

/** Colours already mapped, per function and level: the art is a few dozen palette colours. */
const memo = (f: (c: number, level: number) => number) => {
    const seen = [new Map<number, number>(), new Map<number, number>(), new Map<number, number>()]
    return (c: number, level: number): number => {
        if (level <= 0) return c
        const m = seen[Math.min(2, level)]
        let v = m.get(c)
        if (v === undefined) {
            v = f(c, level)
            m.set(c, v)
        }
        return v
    }
}

/** Sky or sea lit by a flash: its brightness, `level` steps up the flash ramp. */
export const flashSky = memo((c: number, level: number): number => {
    const l = luma(c)
    const rank = l < 22 ? 0 : l < 34 ? 1 : l < 50 ? 2 : l < 75 ? 3 : l < 110 ? 4 : 5
    return FLASH[Math.min(6, rank + (level === 1 ? 0 : 1))]
})

/** The sea under a flash: wave tops catch it, the faces only a little. */
export const flashSea = memo((c: number, level: number): number => {
    const l = luma(c)
    if (l > 120) return level >= 2 ? FLASH[6] : c
    if (l > 60) return FLASH[level >= 2 ? 4 : 3]
    if (l > 30) return FLASH[level >= 2 ? 2 : 1]
    return level >= 2 ? FLASH[1] : FLASH[0]
})

/** Something solid in front of a flash (the ship, the land, the rocks): it goes toward silhouette. */
const SILHOUETTE = rgb(0x130d14)
export const flashSolid = memo((c: number, level: number): number => (level < 2 ? c : mix(c, SILHOUETTE, 0.65)))

/** The lightning at `t`: the strikes in play, and how many steps of light reach a pixel. */
export interface Flash {
    strikes: { bolt: Bolt; sky: number; brightness: number }[]
    level: (x: number, y: number) => number
    /** Some strike is at full brightness: the rain near it shows white. */
    hot: boolean
    /** Changes exactly when anything a flash paints changes: layers lit by it repaint on a new key. */
    key: string
}

export const flashAt = (t: number): Flash | undefined => {
    const strikes = boltsNear(t).map(bolt => {
        const stage = strikeAt(bolt, t)
        return { bolt, sky: stage.sky, brightness: stage.bolt }
    })
    if (!strikes.length) return undefined
    return {
        strikes,
        level: (x, y) => {
            let m = 0
            for (const s of strikes) m = Math.max(m, flashLevelAt(s.bolt, s.sky, x, y))
            return m
        },
        hot: strikes.some(s => s.sky >= 2),
        key: strikes.map(s => `${s.bolt.t}:${s.sky}:${s.brightness}`).join("|"),
    }
}

// ---- the beach --------------------------------------------------------------------------------

/**
 * Surf running up the far beach and drawing back: a few lines of foam along the water's edge, each
 * surging up the sand and sliding down again, broken where the sea is shallow. `shore(x)` is the
 * water's edge at an art column, `x0`..`x1` the beach.
 */
export const paintSurf = (out: PixelBuffer, t: number, x0: number, x1: number, shore: (x: number) => number) => {
    for (let k = 0; k < 3; k++) {
        const u = ((t + k * 600) % 1800) / 1800
        const reach = Math.sin(Math.PI * u)
        const fresh = u < 0.5
        for (let x = x0; x < x1; x++) {
            const wave = 0.5 + 0.5 * Math.sin(x / 7 + k * 2.1 + Math.floor((t + k * 600) / 1800) * 1.7)
            const y = Math.round(shore(x) + 2 - reach * (1 + wave * 2.2))
            if (hash01(x * 7 + k * 101 + Math.floor((t + k * 600) / 1800) * 13) < 0.22) continue
            out.set(x, y, fresh && reach > 0.4 ? FOAM_TONES[2] : FOAM_TONES[0])
            if (fresh && wave > 0.6) out.set(x, y + 1, FOAM_TONES[0])
        }
    }
}
